import { useEffect } from "react";
import { Platform, TextInput, View, type TextInputProps } from "react-native";
import {
  MoonFormField,
  MoonUnitToggle,
  MoonValueInput,
  colors,
  useFieldUnitTextStyle,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { getMarkerUnits, isSameUnit } from "../markerUnits";

type Props = {
  /** Omit when the field sits under its own heading or reads by placeholder. */
  label?: string;
  value: string;
  onChangeValue: (value: string) => void;
  unit: string;
  onChangeUnit: (unit: string) => void;
  /** The marker the value measures. Its known units become the unit choice. */
  marker?: string;
  /** The units to offer, when the field already knows them. */
  units?: readonly string[];
  /**
   * Accept only a number: a decimal keypad, and a comma typed as the decimal
   * mark is kept as a point.
   */
  numeric?: boolean;
  /** Keyboard for a value that may be text, such as "Negative". */
  keyboardType?: TextInputProps["keyboardType"];
  placeholder?: string;
  error?: string;
  accessibilityLabel?: string;
  /** Stable analytics identifier for a press on a unit. */
  unitAction?: string;
  disabled?: boolean;
  testID?: string;
};

function keepNumber(text: string): string {
  const cleaned = text.replace(/,/g, ".").replace(/[^0-9.]/g, "");
  const point = cleaned.indexOf(".");
  return point === -1
    ? cleaned
    : cleaned.slice(0, point + 1) + cleaned.slice(point + 1).replace(/\./g, "");
}

/** A typed unit, for a marker whose units the app does not know. */
function TypedUnit({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (unit: string) => void;
  disabled?: boolean;
}) {
  const t = useTranslations();
  const unitTextStyle = useFieldUnitTextStyle();
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
      <View
        style={{
          width: 1,
          height: 22,
          backgroundColor: "rgba(42,37,34,0.1)",
        }}
      />
      <TextInput
        value={value}
        onChangeText={onChange}
        editable={!disabled}
        placeholder={t("test_result.unit")}
        placeholderTextColor={colors.textFaint}
        accessibilityLabel={t("test_result.unit")}
        autoCapitalize="none"
        autoCorrect={false}
        style={{
          ...unitTextStyle,
          width: 72,
          padding: 0,
          textAlign: "right",
          ...(Platform.OS === "web" ? { outlineStyle: "none" as const } : {}),
        }}
      />
    </View>
  );
}

/**
 * A lab result's value with its unit inside the field: the field for the AMH
 * value, a result being edited, a result added by hand and a monitoring
 * blood value alike.
 *
 * A marker reported in more than one unit offers them as a choice, and a
 * stored unit outside that list stays on offer. A marker with one known unit
 * shows it after the value. A marker the app does not know takes a typed
 * unit at the end of the field. A known marker always carries a unit: an
 * empty one is set to the marker's first.
 */
export function LabValueField({
  label,
  value,
  onChangeValue,
  unit,
  onChangeUnit,
  marker,
  units,
  numeric = false,
  keyboardType,
  placeholder,
  error,
  accessibilityLabel,
  unitAction,
  disabled,
  testID,
}: Props) {
  const t = useTranslations();
  const known = units ?? getMarkerUnits(marker ?? "");
  const current = unit.trim();
  const matched = known.find((candidate) => isSameUnit(candidate, current));
  const offered =
    current && known.length > 0 && !matched ? [...known, current] : known;
  const firstKnown = known[0];

  useEffect(() => {
    if (firstKnown && !current) onChangeUnit(firstKnown);
  }, [current, firstKnown, onChangeUnit]);

  const field = (
    <MoonValueInput
      value={value}
      onChangeText={(text) => onChangeValue(numeric ? keepNumber(text) : text)}
      keyboardType={numeric ? "decimal-pad" : keyboardType}
      placeholder={placeholder}
      hasError={!!error}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel ?? label}
      testID={testID}
      suffix={offered.length === 1 ? offered[0] : undefined}
      trailing={
        offered.length > 1 ? (
          <MoonUnitToggle
            units={offered}
            value={matched ?? (current || null)}
            onChange={onChangeUnit}
            accessibilityLabel={t("test_result.unit")}
            action={unitAction}
            disabled={disabled}
          />
        ) : offered.length === 0 ? (
          <TypedUnit value={unit} onChange={onChangeUnit} disabled={disabled} />
        ) : undefined
      }
    />
  );

  if (!label && !error) return field;
  return (
    <MoonFormField label={label} error={error} disabled={disabled}>
      {field}
    </MoonFormField>
  );
}
